// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the methods paragraph (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`, §Next
 * S5): what was done, written so it can be pasted into a paper's methods
 * section, with an APA-style reference list of every method it cites.
 *
 * **Generated from what the app did, never from what it assumes.** Every
 * sentence comes from one field of {@link MethodsInput}, and a field is
 * filled by the caller only when the app holds that result now (statistics
 * computed, a baseline held, a test switched on). An absent field writes
 * nothing and cites nothing: no sentence, and no reference, describes a
 * step that was not taken.
 *
 * Written in English whatever the interface language, like the citation
 * and the CSV files: it is pasted into the paper, not read in the app.
 * Pure: no DOM, no app state.
 */

import { AYNI_XR_PUBLISHER, formatFrameTime } from './citation'
import { formatFrameIso, type FramePrecision } from '../frameCalendar'
import { STATS_MAX_SLOTS } from './pinStats'

export interface LatLonBox {
  readonly n: number
  readonly s: number
  readonly w: number
  readonly e: number
}

/** How the 8-bit codes decode to values (`types/release-encoding.ts`, or a data-encoded row's linear scale). */
export type MethodsEncoding =
  | { readonly kind: 'linear'; readonly min: number; readonly max: number; readonly step: number }
  | { readonly kind: 'log'; readonly min: number; readonly max: number }
  | { readonly kind: 'classified'; readonly classes: number }

export interface MethodsDataset {
  readonly id: string
  readonly title: string
  /** Who made the data (`citationAuthor`); null when unknown. */
  readonly author: string | null
  /** The `.dsa`'s last revision (else its creation), ISO: the version of the data that was read. */
  readonly revised: string | null
  /** `reanalysis`, `forecast`, … as the `.dsa` writes it. */
  readonly productType: string | null
  readonly units: string | null
  readonly encoding: MethodsEncoding | null
  /** The frame's grid, in cells (texels of the map rectangle). */
  readonly grid: { readonly width: number; readonly height: number } | null
  /** A regional dataset's box; null for the whole globe. */
  readonly bounds: LatLonBox | null
  /** The publisher's frame count (`.dsa` completeness), when it gives one. */
  readonly completeness: {
    readonly expected: number | null
    readonly real: number | null
    readonly filled: number | null
    readonly fillPolicy: string | null
  } | null
}

/** The dataset's time window: its first and last frame. */
export interface MethodsWindow {
  readonly firstMs: number
  readonly lastMs: number
  readonly frames: number | null
}

export interface MethodsRegion {
  /** As the panel names it ("Tropics", "Europe", "The box you drew"). */
  readonly label: string
  readonly kind: 'dataset' | 'view' | 'named' | 'zone' | 'box' | 'country'
  /** Null: the whole dataset. */
  readonly bounds: LatLonBox | null
}

/** Whole-region statistics computed on the frame on screen (Analyze → Statistics). */
export interface MethodsStatistics {
  readonly region: MethodsRegion
  /** Grid cells with data. */
  readonly count: number
  /** Share of the region's cells with data, 0..1. */
  readonly coverage: number
  /** The latitude profile was computed over the same region. */
  readonly zonal: boolean
}

/**
 * How S2 made a period's climatology (`climatology.ts`, `periodRun.ts`),
 * filled from the finished climatology and its run, so the paragraph says
 * exactly what was computed.
 */
export interface MethodsClimatology {
  /** `climatology.PeriodGrouping`: by calendar month (monthly or daily data), month × UTC hour, annual, or one window. */
  readonly grouping: 'monthly' | 'annual' | 'daily' | 'subdaily' | 'window'
  /** Monthly data: months per frame (1, 2, 3, 4, 6). */
  readonly monthsPerFrame: number
  /** Sub-daily data: hours per slot. */
  readonly slotHours: number | null
  /** A window: shorter than two years, or more than eight frames a day. */
  readonly windowReason: 'short' | 'tooFine' | null
  /** Frames of the period the publisher filled, interpolated, estimated or lists as missing: not read, counted as missing. */
  readonly skipped: number
  /** Finished once every group had 80 % of its frames, before all were read. */
  readonly early: boolean
  /** The value's running mean was kept in double precision (groups of more than 255 frames). */
  readonly doubleMean: boolean
  /** Cells kept every `factor`-th texel each way (1: every texel). */
  readonly factor: number
  /** The fewest years in a group the axis visits. */
  readonly minYears: number
  /** Whether any cell has a σ. */
  readonly sigma: boolean
  /** Cell × group values with a σ, and those raised to the one-step floor. */
  readonly sigmaCells: number
  readonly flooredCells: number
  /** The group the frame on screen was read against, in English ("July, 12:00–18:00 UTC"); null when none applies. */
  readonly groupNow: string | null
}

/**
 * The baseline held. `frame` is slice 2's single frame. `period` is the
 * climatology of S2 (a span of frames read as playback passes them), with
 * how it was made (`climatology`).
 */
export type MethodsBaseline =
  | {
      readonly kind: 'frame'
      /** The frame's time as the panel shows it; null for a dataset without dates. */
      readonly frameLabel: string | null
      /** The baseline's dataset when it is not the one measured; null when it is. */
      readonly otherDataset: string | null
      readonly regionLabel: string
      /** The statistics on screen were compared with it (same cells, same units). */
      readonly compared: boolean
    }
  | {
      readonly kind: 'period'
      readonly startLabel: string
      readonly endLabel: string
      /** Frames of the period read during playback, and how many it holds (null when unknown). */
      readonly framesRead: number
      readonly framesInPeriod: number | null
      readonly otherDataset: string | null
      readonly regionLabel: string | null
      /** The frame on screen was compared with it place by place (the "Against the climatology" block). */
      readonly compared: boolean
      readonly climatology: MethodsClimatology
    }

/** AYNI (2026-10-20): the change map's palette, when the viewer chose one other than RdBu, or turned it round. */
export interface MethodsColours {
  readonly palette: 'source' | 'viridis' | 'magma' | 'inferno' | 'plasma' | 'cividis' | 'turbo' | 'coolwarm' | 'grayscale'
  readonly inverted: boolean
}

const PALETTE_NAMES: Record<Exclude<MethodsColours['palette'], 'source'>, string> = {
  viridis: 'Viridis',
  magma: 'Magma',
  inferno: 'Inferno',
  plasma: 'Plasma',
  cividis: 'Cividis',
  turbo: 'Turbo',
  coolwarm: 'Cool–warm',
  grayscale: 'grayscale',
}

/** The change from the baseline drawn on the globe (`changeView.ts`). */
export interface MethodsChange {
  /** `sigma`: the standardised change from a period's climatology, fixed at ±3σ. */
  readonly mode: 'difference' | 'percent' | 'sigma'
  /** The fixed colour range, ± limit; null until the first frame was drawn. */
  readonly limit: number | null
  /** AYNI (2026-10-20): the range the viewer set instead (min < 0 < max); absent or null for ± limit. */
  readonly range?: { readonly min: number; readonly max: number } | null
  /** AYNI (2026-10-20): the palette the viewer chose; absent or null for RdBu. */
  readonly colours?: MethodsColours | null
  /** % change: the |baseline| under which no percentage is shown, and which rule set it (`floorRule`). */
  readonly floor:
    | { readonly value: number; readonly rule: 'steps'; readonly step: number }
    | { readonly value: number; readonly rule: 'typical'; readonly typical: number }
    | null
  /** Drawn on every frame by the graphics card, or for the frame on screen when paused. */
  readonly drawn: 'live' | 'paused'
}

export interface MethodsLine {
  readonly from: { readonly lat: number; readonly lon: number }
  readonly to: { readonly lat: number; readonly lon: number }
  /** AYNI (2026-10-18): along a parallel (the latitude of `from`, eastward) rather than a great circle. */
  readonly path?: 'parallel'
  readonly lengthKm: number
  readonly points: number
}

export interface MethodsHovmoller {
  readonly rowsRead: number
  readonly rows: number
  /** Frames in the window, when the stream says. */
  readonly frames: number | null
  /** Shown as now minus the held baseline at each point. */
  readonly change: boolean
  /** AYNI S2: the baseline is a period: each row against its own frame's group; `sigma`: in σ. */
  readonly against?: 'period'
  readonly sigma?: boolean
}

export interface MethodsPin {
  readonly label: string
  readonly lat: number
  readonly lon: number
  /** Time slots read during playback, and how many the window has. */
  readonly read: number
  readonly slots: number
}

export interface MethodsPins {
  readonly pins: readonly MethodsPin[]
  /** Frames in the window, when known (more frames than slots share them). */
  readonly frames: number | null
  /** What the series are: the dataset's values, or the change the globe shows. */
  readonly values: 'values' | 'difference' | 'percent' | 'sigma' | 'compare'
}

export interface MethodsTrend {
  /** Pins with a trend result (at least ten values). */
  readonly pins: number
  readonly unit: 'decade' | 'year' | 'day' | 'hour'
}

export interface MethodsEmergence {
  readonly pins: number
  readonly startYear: number
  readonly endYear: number
  /** σ entered by the reader, in the series' units; null: the baseline period's own. */
  readonly noise: number | null
  readonly threshold: number
  readonly windowYears: number
  /** The dataset describes itself as a multi-model mean or median. */
  readonly ensemble: boolean
}

export interface MethodsCorrelation {
  /** Pairs of pins with a result. */
  readonly pairs: number
}

export interface MethodsCompare {
  readonly otherTitle: string
  readonly otherUnits: string | null
  /** B to A's units: A = factor × B + offset; null when no conversion was needed. */
  readonly conversion: { readonly factor: number; readonly offset: number } | null
  /** Degrees per cell of the common grid. */
  readonly cellDegrees: number
  /** The two frames' dates as the panel shows them, when both have dates. */
  readonly frames: { readonly a: string; readonly b: string } | null
  /** A − B drawn on the globe. */
  readonly mapped: boolean
  /** Where B comes from: a real-time overlay on the same globe, or another globe of the layout. */
  readonly source?: 'overlay' | 'globe'
  /** How the frames were matched: the same date (within half the coarser time step), or as each globe shows them. */
  readonly pairing?: 'time' | 'shown'
  /** What the globe shows: A − B, or (A − B) / |B| as a % of B (ratio quantities only). */
  readonly mode?: 'difference' | 'percent' | null
  /** AYNI (2026-10-20): the colour range the viewer set for the map; absent or null for the automatic one. */
  readonly range?: { readonly min: number; readonly max: number } | null
  /** AYNI (2026-10-20): the map's palette, when not RdBu. */
  readonly colours?: MethodsColours | null
  /** % of B: the |B| under which no percentage is drawn, and the rule that set it (`floorRule`). */
  readonly floor?:
    | { readonly value: number; readonly rule: 'steps'; readonly step: number }
    | { readonly value: number; readonly rule: 'typical'; readonly typical: number }
    | null
  /** Both are ratio quantities: the bias is also given as a % of B's mean. */
  readonly ratio?: boolean
  /** The region was read on each side (mean, median, 10th and 90th percentiles). */
  readonly sides?: boolean
}

/** The area over a threshold, through time (S7, `threshold.ts`), once a frame has been measured into it. */
export interface MethodsThreshold {
  readonly mode: 'value' | 'difference' | 'sigma'
  readonly comparator: 'above' | 'below'
  readonly threshold: number
  readonly units: string | null
  /** The region in English, as the statistics name it. */
  readonly region: string
  /** Frames measured into the series, and how many can be (null: no time axis, only the frame on screen). */
  readonly framesRead: number
  readonly frames: number | null
  /** Frames the publisher filled, interpolated, estimated or lists as missing: not measured. */
  readonly skipped: number
  /** What a change is measured from. */
  readonly against: 'frame' | 'period' | null
}

/** Colours set in Tools → View, when they are not the dataset's own. */
export interface MethodsDisplay {
  readonly palette: string
  readonly inverted: boolean
  /** The values the colour ramp spans, when narrowed. */
  readonly range: { readonly min: number; readonly max: number } | null
  /** Values outside this band hidden; null bounds are open. */
  readonly threshold: { readonly min: number | null; readonly max: number | null } | null
}

/** A figure exported from the figure dialog (§Next S4). */
export interface MethodsFigure {
  readonly kind: 'globe' | 'equal-earth'
  readonly widthMm: number
  readonly dpi: number
  readonly widthPx: number
  readonly heightPx: number
  /** The map's raster in the figure, and the render it came from (larger than the raster: none). */
  readonly mapPx: { readonly width: number; readonly height: number }
  readonly sourcePx: { readonly width: number; readonly height: number } | null
  readonly pins: number
  readonly display: MethodsDisplay | null
}

export interface MethodsInput {
  readonly dataset: MethodsDataset
  /** The publisher's credit line, as written. */
  readonly credit: string | null
  readonly sources: readonly { readonly label: string | null; readonly licence: string | null }[]
  /** The frame on screen, epoch ms; null for a dataset without dates. */
  readonly frameMs: number | null
  /** AYNI: what a frame's time names on this dataset (its year, month…; `frameCalendar.ts`); null: the instant. */
  readonly framePrecision?: FramePrecision | null
  readonly window: MethodsWindow | null
  readonly statistics: MethodsStatistics | null
  readonly baseline: MethodsBaseline | null
  readonly change: MethodsChange | null
  readonly line: MethodsLine | null
  readonly hovmoller: MethodsHovmoller | null
  readonly pins: MethodsPins | null
  readonly trend: MethodsTrend | null
  readonly emergence: MethodsEmergence | null
  readonly correlation: MethodsCorrelation | null
  readonly compare: MethodsCompare | null
  readonly threshold?: MethodsThreshold | null
  readonly figure: MethodsFigure | null
  /** `AYNI XR <version>`. */
  readonly software: string
  readonly softwareDoi: string | null
  readonly viewUrl: string
  /** The data's citation (`citationText`), listed with the references. */
  readonly citation: string
  readonly accessed: Date
}

/** A method the paragraph can cite. */
export type MethodRef =
  | 'santer2000' | 'nychka2000' | 'mann1945' | 'kendall1975' | 'hamedRao1998' | 'sen1968' | 'gilbert1987'
  | 'bretherton1999' | 'hawkinsSutton2012' | 'hovmoller1949' | 'taylor2001' | 'harrowerBrewer2003' | 'savric2019'
  | 'wmo2017' | 'welford1962' | 'west1979' | 'moritz2000'

/** APA 7 entries, checked against the publishers' records. */
export const METHOD_REFERENCES: Readonly<Record<MethodRef, string>> = {
  bretherton1999: 'Bretherton, C. S., Widmann, M., Dymnikov, V. P., Wallace, J. M., & Bladé, I. (1999). The effective number of spatial degrees of freedom of a time-varying field. Journal of Climate, 12(7), 1990–2009. https://doi.org/10.1175/1520-0442(1999)012<1990:TENOSD>2.0.CO;2',
  gilbert1987: 'Gilbert, R. O. (1987). Statistical methods for environmental pollution monitoring. Van Nostrand Reinhold.',
  hamedRao1998: 'Hamed, K. H., & Rao, A. R. (1998). A modified Mann-Kendall trend test for autocorrelated data. Journal of Hydrology, 204(1–4), 182–196. https://doi.org/10.1016/S0022-1694(97)00125-X',
  harrowerBrewer2003: 'Harrower, M., & Brewer, C. A. (2003). ColorBrewer.org: An online tool for selecting colour schemes for maps. The Cartographic Journal, 40(1), 27–37. https://doi.org/10.1179/000870403235002042',
  hawkinsSutton2012: 'Hawkins, E., & Sutton, R. (2012). Time of emergence of climate signals. Geophysical Research Letters, 39(1), L01702. https://doi.org/10.1029/2011GL050087',
  hovmoller1949: 'Hovmöller, E. (1949). The trough-and-ridge diagram. Tellus, 1(2), 62–66. https://doi.org/10.3402/tellusa.v1i2.8498',
  kendall1975: 'Kendall, M. G. (1975). Rank correlation methods (4th ed.). Charles Griffin.',
  moritz2000: 'Moritz, H. (2000). Geodetic Reference System 1980. Journal of Geodesy, 74(1), 128–133. https://doi.org/10.1007/s001900050278',
  mann1945: 'Mann, H. B. (1945). Nonparametric tests against trend. Econometrica, 13(3), 245–259. https://doi.org/10.2307/1907187',
  nychka2000: 'Nychka, D., Buchberger, R., Wigley, T. M. L., Santer, B. D., Taylor, K. E., & Jones, R. (2000). Confidence intervals for trend estimates with autocorrelated observations [Unpublished manuscript]. National Center for Atmospheric Research.',
  santer2000: 'Santer, B. D., Wigley, T. M. L., Boyle, J. S., Gaffen, D. J., Hnilo, J. J., Nychka, D., Parker, D. E., & Taylor, K. E. (2000). Statistical significance of trends and trend differences in layer-average atmospheric temperature time series. Journal of Geophysical Research: Atmospheres, 105(D6), 7337–7356. https://doi.org/10.1029/1999JD901105',
  savric2019: 'Šavrič, B., Patterson, T., & Jenny, B. (2019). The Equal Earth map projection. International Journal of Geographical Information Science, 33(3), 454–465. https://doi.org/10.1080/13658816.2018.1504949',
  sen1968: 'Sen, P. K. (1968). Estimates of the regression coefficient based on Kendall’s tau. Journal of the American Statistical Association, 63(324), 1379–1389. https://doi.org/10.1080/01621459.1968.10480934',
  welford1962: 'Welford, B. P. (1962). Note on a method for calculating corrected sums of squares and products. Technometrics, 4(3), 419–420. https://doi.org/10.1080/00401706.1962.10490022',
  west1979: 'West, D. H. D. (1979). Updating mean and variance estimates: An improved method. Communications of the ACM, 22(9), 532–535. https://doi.org/10.1145/359146.359153',
  wmo2017: 'World Meteorological Organization. (2017). WMO guidelines on the calculation of climate normals (WMO-No. 1203). World Meteorological Organization.',
  taylor2001: 'Taylor, K. E. (2001). Summarizing multiple aspects of model performance in a single diagram. Journal of Geophysical Research: Atmospheres, 106(D7), 7183–7192. https://doi.org/10.1029/2000JD900719',
}

export interface MethodsResult {
  /** The paragraph, ready to paste. */
  readonly paragraph: string
  /** The methods it cites, in the order first cited. */
  readonly cited: readonly MethodRef[]
  /** The reference list: the data, AYNI XR and every method cited, alphabetical. */
  readonly references: readonly string[]
}

// ------------------------------------------------------------------ formatting

const MINUS = '−'

/** A number in plain English notation, `sig` significant digits, a true minus sign. */
export function num(value: number, sig = 3): string {
  if (!Number.isFinite(value)) return 'n/a'
  if (value === 0) return '0'
  const rounded = Number(value.toPrecision(sig))
  const text = Math.abs(rounded) >= 1e-4 && Math.abs(rounded) < 1e15
    ? Math.abs(rounded).toLocaleString('en-GB', { maximumFractionDigits: 12, useGrouping: Math.abs(rounded) >= 10_000 })
    : String(Math.abs(rounded))
  return `${rounded < 0 ? MINUS : ''}${text}`
}

/** A value with its units: "0.0941 °C", "12 %". */
function amount(value: number, units: string | null | undefined, sig = 3): string {
  return units ? `${num(value, sig)} ${units}` : num(value, sig)
}

export function latText(lat: number): string {
  return `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? 'N' : 'S'}`
}

export function lonText(lon: number): string {
  return `${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? 'E' : 'W'}`
}

function point(p: { lat: number; lon: number }): string {
  return `${latText(p.lat)}, ${lonText(p.lon)}`
}

/** "23.44° S to 23.44° N, all longitudes", or the box's four sides. */
export function boxText(b: LatLonBox): string {
  const lats = `${latText(b.s)} to ${latText(b.n)}`
  const allLon = Math.abs(b.e - b.w) >= 360 - 1e-6 || (b.w <= -180 && b.e >= 180)
  return allLon ? `${lats}, all longitudes` : `${lats}, ${lonText(b.w)} to ${lonText(b.e)}`
}

/**
 * A date for a window or a series: at the frames' precision when known (a
 * yearly window runs from 1950 to 2100), else the day alone when the time
 * is midnight UTC.
 */
export function dateText(ms: number, precision?: FramePrecision | null): string {
  if (precision === 'year' || precision === 'month' || precision === 'day') return formatFrameIso(ms, precision)
  const iso = new Date(ms).toISOString()
  return iso.slice(11, 16) === '00:00' ? iso.slice(0, 10) : formatFrameTime(ms)
}

function count(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`
}

function sentence(text: string): string {
  const s = text.trim()
  return /[.!?]$/.test(s) ? s : `${s}.`
}

// ------------------------------------------------------------------ the paragraph

/**
 * The paragraph and its references for one state of the app. Each part is
 * written only from its own field; see the module comment.
 */
export function buildMethods(input: MethodsInput): MethodsResult {
  const parts: string[] = []
  const cited: MethodRef[] = []
  const cite = (...refs: MethodRef[]) => {
    for (const r of refs) if (!cited.includes(r)) cited.push(r)
  }
  const ds = input.dataset
  const units = ds.units

  // The data and its version.
  {
    let s = `We used ${ds.title}`
    if (ds.author) s += ` (${ds.author})`
    if (ds.productType) s += `, a ${ds.productType} product`
    s += `, AYNI XR data set ${ds.id}`
    if (ds.revised && !Number.isNaN(Date.parse(ds.revised))) s += `, in the version its publisher last revised on ${new Date(ds.revised).toISOString().slice(0, 10)}`
    parts.push(sentence(s))
    if (input.credit?.trim()) parts.push(sentence(`Credit: ${input.credit.trim()}`))
    const sources = input.sources.filter((src) => src.label || src.licence)
    if (sources.length) {
      parts.push(sentence(`Sources: ${sources.map((src) => (src.label && src.licence ? `${src.label} (${src.licence})` : src.label ?? src.licence)).join('; ')}`))
    }
  }

  // The software and the encoding it reads.
  {
    const doi = input.softwareDoi?.trim()
    let s = `The data were viewed and measured in ${input.software} (${AYNI_XR_PUBLISHER}${doi ? `; https://doi.org/${doi}` : ''}) on ${input.accessed.toISOString().slice(0, 10)}`
    if (ds.grid) s += `, on a grid of ${ds.grid.width.toLocaleString('en-GB')} × ${ds.grid.height.toLocaleString('en-GB')} cells`
    s += ds.bounds ? ` covering ${boxText(ds.bounds)}` : ds.grid ? ' covering the globe' : ''
    parts.push(sentence(s))
    const enc = ds.encoding
    if (enc?.kind === 'linear') {
      parts.push(sentence(`Values run linearly from ${amount(enc.min, units, 4)} to ${amount(enc.max, units, 4)}, rounded to steps of ${amount(enc.step, units)}, so differences smaller than one step are not resolved`))
    } else if (enc?.kind === 'log') {
      parts.push(sentence(`Values run on a logarithmic scale from ${amount(enc.min, units, 4)} to ${amount(enc.max, units, 4)}, rounded to steps of a fixed ratio that grow in absolute size with the value`))
    } else if (enc?.kind === 'classified') {
      parts.push(sentence(`The values stand for ${count(enc.classes, 'class', 'classes')}, not amounts`))
    }
  }

  // Time: the window and the frame.
  if (input.window) {
    const w = input.window
    let s = `The dataset's window runs from ${dateText(w.firstMs, input.framePrecision)} to ${dateText(w.lastMs, input.framePrecision)}`
    if (w.frames !== null && w.frames > 0) s += ` (${count(w.frames, 'frame')})`
    parts.push(sentence(s))
  }
  const c = ds.completeness
  if (c && c.expected !== null && c.real !== null) {
    let s = `Its publisher lists ${c.real.toLocaleString('en-GB')} of ${c.expected.toLocaleString('en-GB')} frames as observed or modelled`
    if (c.filled) s += ` and ${c.filled.toLocaleString('en-GB')} as filled${c.fillPolicy ? ` (${c.fillPolicy.replace(/_/g, ' ')})` : ''}`
    parts.push(sentence(s))
  }
  const frameText = input.frameMs !== null ? formatFrameTime(input.frameMs, input.framePrecision) : null

  // Statistics over a region, on the frame on screen.
  if (input.statistics) {
    const st = input.statistics
    const where = regionText(st.region)
    parts.push(sentence(`Statistics weighted by grid-cell area (mean, median, minimum, maximum, 10th and 90th percentiles and standard deviation) were computed over ${where}${frameText ? ` on the frame of ${frameText}` : ' on the frame on screen'}, from ${count(st.count, 'grid cell')} with data (${num(st.coverage * 100, 3)} % of the region's cells)`))
    if (st.zonal) parts.push('A latitude profile (the mean of each grid row) was computed over the same region.')
  }

  // The baseline.
  const b = input.baseline
  if (b) {
    if (b.kind === 'frame') {
      const of = b.otherDataset ? ` of ${b.otherDataset}` : ''
      parts.push(sentence(`A baseline was held at the frame of ${b.frameLabel ?? 'a frame without a date'}${of}, over ${b.regionLabel}`))
    } else {
      parts.push(...periodSentences(b, cite))
    }
    if (b.kind === 'period') {
      if (b.compared && input.statistics) {
        const c = b.climatology
        const group = c.groupNow ? ` of ${c.groupNow}` : ''
        const sigmaStats = c.sigma ? ', the mean standardised change and the share of that area beyond ±2 σ' : ''
        parts.push(sentence(`The frame was compared with the climatology${group} place by place, over the grid cells where both have data: the area-weighted mean change${sigmaStats}; the frame's extremes and percentiles were not compared with a field of means`))
        if (input.statistics.zonal) parts.push('The latitude profiles of the frame and of the climatology were computed over the same cells.')
      }
    } else if (b.compared && input.statistics) {
      parts.push('Each statistic is reported with its difference from the baseline (now minus baseline) over the same grid cells and in the same units; a difference smaller than one quantisation step is reported as within the data\'s resolution.')
      if (input.statistics.zonal) parts.push('The latitude profile is compared with the baseline\'s band by band.')
    }
  }

  // The change map.
  const ch = input.change
  if (ch && b && (ch.mode !== 'sigma' || b.kind === 'period')) {
    if (isRdBu(ch.colours)) cite('harrowerBrewer2003')
    if (b.kind === 'period') {
      const group = groupWord(b.climatology)
      if (ch.mode === 'sigma') {
        parts.push(sentence(`The standardised change, (now − mean) / σ for the frame's ${group}, was mapped grid cell by grid cell on ${scaleWords(ch.colours)} ${ch.range ? `fixed at ${num(ch.range.min)} to +${num(ch.range.max)} σ, a range the viewer set (each side scaled to its own end, ${noChangeWords(ch.colours)}),` : `fixed at ±${num(ch.limit ?? 3)} σ`} for every frame`))
        parts.push('Cells where only one of the frame and the climatology has data, or without σ, are grey.')
      } else {
        const what = ch.mode === 'difference'
          ? `as now minus the climatology's mean for the frame's ${group}${units ? `, in ${units}` : ''}`
          : `as the percentage change from the climatology's mean for the frame's ${group}, 100 × (now − mean) / |mean|`
        parts.push(sentence(`The change from the climatology was mapped grid cell by grid cell ${what}${changeRange(ch, units)}`))
        if (ch.mode === 'percent' && ch.floor) {
          const rule = ch.floor.rule === 'steps'
            ? `two quantisation steps of ${amount(ch.floor.step, units)}`
            : `2 % of the climatology means' typical magnitude, their 95th percentile over every ${group} of ${amount(ch.floor.typical, units)}`
          parts.push(sentence(`Cells where only one of the frame and the climatology has data, or where |mean| is below ${amount(ch.floor.value, units)} (${rule}, whichever is larger), are grey`))
        } else {
          parts.push('Cells where only one of the frame and the climatology has data are grey.')
        }
      }
    } else {
      const what = ch.mode === 'difference'
        ? `as now minus baseline${units ? `, in ${units}` : ''}`
        : 'as the percentage change, 100 × (now − baseline) / |baseline|'
      parts.push(sentence(`The change from the baseline was mapped grid cell by grid cell ${what}${changeRange(ch, units)}`))
      if (ch.mode === 'percent' && ch.floor) {
        const rule = ch.floor.rule === 'steps'
          ? `two quantisation steps of ${amount(ch.floor.step, units)}`
          : `2 % of the baseline's typical magnitude, its 95th percentile of ${amount(ch.floor.typical, units)}`
        parts.push(sentence(`Cells where only one of the two frames has data, or where |baseline| is below ${amount(ch.floor.value, units)} (${rule}, whichever is larger), are grey`))
      } else {
        parts.push('Cells where only one of the two frames has data are grey.')
      }
    }
    parts.push(ch.drawn === 'live'
      ? 'The change was drawn on every frame from the same colour table.'
      : 'The change was drawn for the frame on screen once playback stopped.')
  }

  // The line and its Hovmöller diagram.
  if (input.line) {
    const l = input.line
    parts.push(sentence(l.path === 'parallel'
      ? `Values were sampled along the parallel at ${num(l.from.lat, 4)}°, eastward from ${point(l.from)} to ${point(l.to)} (${num(l.lengthKm, 4)} km), at ${count(l.points, 'point')}, one per grid cell crossed`
      : `Values were sampled along a great-circle line from ${point(l.from)} to ${point(l.to)} (${num(l.lengthKm, 4)} km), at ${count(l.points, 'point')}, one per grid cell crossed`))
  }
  if (input.hovmoller && input.line) {
    const h = input.hovmoller
    cite('hovmoller1949')
    let s = `A Hovmöller diagram (Hovmöller, 1949) was recorded along the line as the dataset played: ${h.rowsRead.toLocaleString('en-GB')} of ${count(h.rows, 'row')} read`
    s += h.frames !== null && h.frames > h.rows ? ` (the window's ${h.frames.toLocaleString('en-GB')} frames share rows; the last frame read in a row is kept)` : ', one row per frame'
    if (h.change && h.against === 'period') {
      s += h.sigma
        ? ", shown as the standardised change at each point, each row against the climatology's mean and σ for the group of the frame read into it"
        : ", shown as now minus the climatology's mean at each point, each row against the group of the frame read into it"
    } else if (h.change) s += ', shown as now minus the baseline at each point'
    parts.push(sentence(s))
  }

  // Pins and their series.
  const pins = input.pins
  if (pins && pins.pins.length) {
    const list = pins.pins.map((p) => `${p.label} (${point(p)}; ${p.read.toLocaleString('en-GB')} of ${p.slots.toLocaleString('en-GB')} time steps read)`).join(', ')
    parts.push(sentence(`Time series were read at ${count(pins.pins.length, 'point')}, each the value of the grid cell under it as the dataset played: ${list}`))
    const slots = Math.max(...pins.pins.map((p) => p.slots))
    if (pins.frames !== null && pins.frames > slots) {
      parts.push(sentence(`The window's ${pins.frames.toLocaleString('en-GB')} frames share ${slots.toLocaleString('en-GB')} time steps; the last frame read in a step is kept`))
    }
    if (b?.kind === 'period' && (pins.values === 'difference' || pins.values === 'percent' || pins.values === 'sigma')) {
      const group = groupWord(b.climatology)
      const what = pins.values === 'sigma' ? 'standardised change' : pins.values === 'percent' ? 'percentage change' : 'change'
      parts.push(sentence(`The series are given as the ${what} from the climatology at each point, each time step against the mean${pins.values === 'sigma' ? ' and σ' : ''} of its own ${group}`))
    } else if (pins.values === 'difference') parts.push('The series are given as the change from the baseline at each point.')
    else if (pins.values === 'percent') parts.push('The series are given as the percentage change from the baseline at each point.')
    else if (pins.values === 'compare' && input.compare) parts.push(sentence(input.compare.mode === 'percent' ? `The series are given as ${ds.title} minus ${input.compare.otherTitle} as a percentage of ${input.compare.otherTitle} at each point and time step` : `The series are given as ${ds.title} minus ${input.compare.otherTitle} at each point and time step`))
  }

  // Tests on the series.
  if (input.trend && input.trend.pins > 0) {
    cite('santer2000', 'nychka2000', 'mann1945', 'kendall1975', 'hamedRao1998', 'sen1968', 'gilbert1987')
    parts.push(sentence(`For ${input.trend.pins === 1 ? 'the series' : `each of ${input.trend.pins} series`}, a linear trend per ${input.trend.unit} was estimated by least squares, with its 95 % confidence interval widened for lag-1 autocorrelation (Santer et al., 2000) using the effective sample size of Nychka et al. (2000), and tested with the Mann–Kendall test (Mann, 1945; Kendall, 1975) with Kendall's tie correction and the variance correction of Hamed and Rao (1998); Sen's (1968) slope is given with its confidence interval after Gilbert (1987)`))
    parts.push('A trend is called rising or falling only when both tests find it at the 5 % level with the same sign.')
  }
  if (input.emergence && input.emergence.pins > 0) {
    const e = input.emergence
    cite('hawkinsSutton2012')
    const noise = e.noise === null
      ? 'the standard deviation of the baseline period\'s values about their own least-squares line (never below one quantisation step)'
      : `${amount(e.noise, units)}, as entered`
    parts.push(sentence(`Time of emergence followed Hawkins and Sutton (2012): the first year from which a centred ${e.windowYears}-year running mean stays more than ${e.threshold} σ from the mean of the baseline period ${e.startYear}–${e.endYear} until the last year it can be computed, σ being ${noise}`))
    if (e.ensemble && e.noise === null) parts.push('The dataset describes itself as a multi-model mean or median, whose year-to-year variability is smaller than that of any single realisation, so these years are likely too early.')
  }
  if (input.correlation && input.correlation.pairs > 0) {
    cite('bretherton1999')
    parts.push(sentence(`Correlations between ${input.correlation.pairs === 1 ? 'the two series' : `${input.correlation.pairs} pairs of series`} (Pearson's r and Spearman's ρ) were tested with the effective number of independent values of Bretherton et al. (1999), also after removing each series' least-squares trend; the strongest lag is reported with a Bonferroni-corrected p-value`))
  }

  // AYNI: on a long window the trend and correlation tests run on fewer steps than the frames read.
  const testedSlots = pins && pins.pins.length ? Math.max(...pins.pins.map((p) => p.slots)) : 0
  if (testedSlots > STATS_MAX_SLOTS && ((input.trend && input.trend.pins > 0) || (input.correlation && input.correlation.pairs > 0))) {
    parts.push(sentence(`The trend and correlation tests used ${STATS_MAX_SLOTS.toLocaleString('en-GB')} equal time steps across the window, each the mean of the frames read in it`))
  }

  // Compare with another dataset.
  if (input.compare) {
    const cmp = input.compare
    cite('taylor2001')
    const from = cmp.source === 'globe' ? ' on another globe of the same layout' : cmp.source === 'overlay' ? ', a real-time overlay on the same globe' : ''
    let s = `${ds.title} (A) was compared with ${cmp.otherTitle} (B)${from}: both were averaged onto a common grid of ${num(cmp.cellDegrees, 3)}°, the coarser of the two, a cell counting where at least half of it has data`
    if (cmp.conversion) {
      const { factor, offset } = cmp.conversion
      s += `; B was converted${cmp.otherUnits && units ? ` from ${cmp.otherUnits} to ${units}` : ''} as ${num(factor, 6)} × B${offset ? ` ${offset < 0 ? MINUS : '+'} ${num(Math.abs(offset), 6)}` : ''}`
    }
    if (cmp.pairing === 'shown') {
      s += cmp.frames ? `; the frames compared are those each globe showed, ${cmp.frames.a} (A) and ${cmp.frames.b} (B), whose dates were stated but not matched` : '; the frames compared are those each globe showed, without dates'
    } else {
      s += cmp.frames ? `; the frames compared are ${cmp.frames.a} (A) and ${cmp.frames.b} (B), paired by date within half the coarser time step` : '; the frames have no dates to pair'
    }
    parts.push(sentence(s))
    parts.push(`Bias, root-mean-square difference, centred root-mean-square difference (Taylor, 2001) and pattern correlation were computed over the cells where both have data, weighted by cell area${cmp.ratio ? ", and the bias also as a percentage of B's mean" : ''}.`)
    if (cmp.sides) parts.push("The region's area-weighted mean, median and 10th and 90th percentiles were read on each side.")
    if (cmp.mapped && cmp.mode === 'percent') {
      if (isRdBu(cmp.colours)) cite('harrowerBrewer2003')
      parts.push(sentence(`The difference was mapped as a percentage of B, 100 × (A − B) / |B|, on ${scaleWords(cmp.colours)}${compareRangeWords(cmp, '%')}, grey where only one has data${cmp.floor ? ` or where |B| is below ${amount(cmp.floor.value, units)} (${cmp.floor.rule === 'steps' ? `two quantisation steps of B, ${amount(cmp.floor.step, units)}` : `2 % of B's typical magnitude, its 95th percentile of ${amount(cmp.floor.typical, units)}`}, whichever is larger)` : ''}`))
    } else if (cmp.mapped) {
      if (isRdBu(cmp.colours)) cite('harrowerBrewer2003')
      parts.push(sentence(`A − B was mapped on ${scaleWords(cmp.colours)}${compareRangeWords(cmp, units)}, grey where only one has data`))
    }
  }

  // The area over a threshold.
  const thr = input.threshold
  if (thr && (thr.mode === 'value' || (b && (thr.mode !== 'sigma' || b.kind === 'period')))) {
    cite('moritz2000')
    const what = thr.mode === 'value'
      ? 'the value'
      : thr.mode === 'sigma'
        ? 'the standardised change from the climatology'
        : thr.against === 'period' ? 'the change from the climatology' : 'the change from the held frame'
    const level = thr.mode === 'sigma' ? `${num(thr.threshold, 4)} σ` : amount(thr.threshold, units, 4)
    const when = thr.frames !== null
      ? `in each frame read as the dataset played (${thr.framesRead.toLocaleString('en-GB')} of ${count(thr.frames, 'frame')}${thr.skipped ? `; the ${thr.skipped.toLocaleString('en-GB')} frames the publisher filled, interpolated, estimated or lists as missing were not measured` : ''})`
      : 'on the frame on screen'
    const band = thr.mode === 'sigma' ? 'one quantisation step divided by the cell\'s σ' : thr.mode === 'difference' && thr.against === 'frame' ? 'two quantisation steps (both frames are quantised)' : 'one quantisation step'
    parts.push(sentence(`The area of ${thr.region} where ${what} was strictly ${thr.comparator} ${level} was measured ${when}, on the dataset's own grid without regridding (whole cells whose centres fall in the region), each cell's area taken on a sphere of the WGS84 authalic radius, 6371.0072 km (Moritz, 2000); cells closer to the threshold than ${band} could lie on either side of it, so the area is also given as a range`))
  }

  // The figure.
  if (input.figure) {
    const f = input.figure
    if (f.kind === 'equal-earth') cite('savric2019')
    const what = f.kind === 'globe'
      ? 'the globe as rendered on screen'
      : 'the whole frame on the Equal Earth projection (Šavrič et al., 2019)'
    let s = `The figure shows ${what}, ${num(f.widthMm, 3)} mm wide at ${f.dpi} dpi (${f.widthPx} × ${f.heightPx} pixels); its map is a raster of ${f.mapPx.width} × ${f.mapPx.height} pixels`
    // Smaller by more than rounding (1 %): the map was enlarged from the render.
    if (f.sourcePx && (f.sourcePx.width < f.mapPx.width * 0.99 || f.sourcePx.height < f.mapPx.height * 0.99)) {
      s += `, enlarged from a render of ${f.sourcePx.width} × ${f.sourcePx.height} pixels`
    }
    if (ds.grid) s += `, from a data frame of ${ds.grid.width.toLocaleString('en-GB')} × ${ds.grid.height.toLocaleString('en-GB')} grid cells`
    if (f.pins) s += `, with ${count(f.pins, 'pin')} marked`
    parts.push(sentence(s))
    const d = f.display
    if (d) {
      const bits = [`the ${d.palette} palette${d.inverted ? ', inverted' : ''}`]
      if (d.range) bits.push(`the colour ramp spanning ${amount(d.range.min, units, 4)} to ${amount(d.range.max, units, 4)}`)
      if (d.threshold && (d.threshold.min !== null || d.threshold.max !== null)) {
        const lo = d.threshold.min !== null ? `below ${amount(d.threshold.min, units, 4)}` : null
        const hi = d.threshold.max !== null ? `above ${amount(d.threshold.max, units, 4)}` : null
        bits.push(`values ${[lo, hi].filter(Boolean).join(' or ')} hidden`)
      }
      parts.push(sentence(`Colours: ${bits.join('; ')}`))
    }
  }

  // Limits that follow from what was read.
  if ((pins && pins.pins.length) || input.hovmoller) {
    parts.push('Series hold only the frames read during playback; a frame not read is missing, never interpolated.')
  }

  parts.push(sentence(`The view can be reopened at ${input.viewUrl}`))

  const references = [input.citation.trim(), softwareReference(input), ...cited.map((r) => METHOD_REFERENCES[r])]
    .filter((r) => r.length > 0)
    .sort((x, y) => x.localeCompare(y, 'en', { sensitivity: 'base' }))
  return { paragraph: parts.join(' '), cited, references }
}

/** AYNI: whether the map is drawn in RdBu, the default (turned round or not). */
function isRdBu(colours: MethodsColours | null | undefined): boolean {
  return !colours || colours.palette === 'source'
}

/** AYNI (2026-10-20): the scale the change was mapped on, in words. */
function scaleWords(colours: MethodsColours | null | undefined): string {
  const reversed = colours?.inverted ? ', reversed (red for a decrease)' : ''
  if (isRdBu(colours)) return `the ColorBrewer RdBu diverging scale (Harrower & Brewer, 2003)${reversed}`
  return `the ${PALETTE_NAMES[colours!.palette as Exclude<MethodsColours['palette'], 'source'>]} palette${colours!.inverted ? ', reversed' : ''}, its middle at no change`
}

/** What no change looks like on that scale. */
function noChangeWords(colours: MethodsColours | null | undefined): string {
  return isRdBu(colours) ? 'so no change stays white' : "so no change stays at the palette's middle"
}

/** AYNI: Compare's range, when the viewer set one. */
function compareRangeWords(cmp: MethodsCompare, units: string | null): string {
  if (!cmp.range) return ''
  const end = (v: number) => (units === '%' ? `${num(v)} %` : amount(v, units))
  return ` fixed at ${end(cmp.range.min)} to +${end(cmp.range.max)}, a range the viewer set (each side scaled to its own end, ${noChangeWords(cmp.colours)})`
}

/** The fixed colour range of a change in words; the 98th-percentile rule. */
function changeRange(ch: MethodsChange, units: string | null): string {
  if (ch.range) {
    const end = (v: number) => (ch.mode === 'percent' ? `${num(v)} %` : amount(v, units))
    return `, on ${scaleWords(ch.colours)} fixed at ${end(ch.range.min)} to +${end(ch.range.max)} for every frame, a range the viewer set (each side scaled to its own end, ${noChangeWords(ch.colours)})`
  }
  return ch.limit !== null
    ? `, on ${scaleWords(ch.colours)} fixed at ±${ch.mode === 'percent' ? `${num(ch.limit)} %` : amount(ch.limit, units)} for every frame (the 98th percentile of the absolute change on the first frame drawn, rounded up to 1, 2, 2.5 or 5 × 10ⁿ)`
    : `, on ${scaleWords(ch.colours)}`
}

/** What a frame of this climatology is compared with: "calendar month", "calendar month and 6-hour UTC slot", "period". */
function groupWord(c: MethodsClimatology): string {
  if (c.grouping === 'monthly' || c.grouping === 'daily') return 'calendar month'
  if (c.grouping === 'subdaily') return `calendar month and ${c.slotHours !== null ? `${num(c.slotHours, 3)}-hour ` : ''}UTC slot`
  return c.grouping === 'window' ? 'window' : 'period'
}

/**
 * A period baseline as S2 computed it: what was read, how the frames were
 * grouped, the 80 % rule, the running sums, σ and its floor, storage and
 * thinning. Only what applies to this climatology is said.
 */
function periodSentences(b: Extract<MethodsBaseline, { kind: 'period' }>, cite: (...refs: MethodRef[]) => void): string[] {
  const c = b.climatology
  const out: string[] = []
  const of = b.otherDataset ? ` of ${b.otherDataset}` : ''
  const read = b.framesInPeriod !== null
    ? `${b.framesRead.toLocaleString('en-GB')} of its ${count(b.framesInPeriod, 'frame')}`
    : count(b.framesRead, 'frame')
  const span = c.grouping === 'window' ? `The whole window, ${b.startLabel} to ${b.endLabel}${of}, was held as the baseline` : `A baseline period from ${b.startLabel} to ${b.endLabel}${of} was held as a climatology`
  let s = `${span}, from ${read} read as playback passed them`
  if (c.skipped) s += `; ${count(c.skipped, 'frame')} the publisher lists as filled, interpolated, estimated or missing were not read and count as missing`
  if (c.early && c.grouping === 'window') s += '; it was finished once 80 % of its frames were read'
  else if (c.early) s += `; it was finished once every ${groupWord(c)} had at least 80 % of its frames read`
  out.push(sentence(s))
  const group = groupWord(c)
  if (c.grouping === 'monthly') {
    cite('wmo2017')
    const every = c.monthsPerFrame > 1 ? ` (one frame every ${c.monthsPerFrame} months, so only the months the axis visits)` : ''
    out.push(sentence(`Each grid cell's mean was computed for each calendar month${every}, each frame against the mean of its own month, as climatological normals are made (WMO, 2017); a frame's month was taken from its position on the time axis, not from a date that drifts with a mean-month time step`))
  } else if (c.grouping === 'daily') {
    cite('wmo2017')
    out.push(sentence('Each grid cell\'s mean was computed for each calendar month from the frames in it (a frame belongs to the month of its middle), each frame against the mean of its own month'))
  } else if (c.grouping === 'subdaily') {
    cite('wmo2017')
    out.push(sentence(`Each grid cell's mean was computed for each calendar month and each ${c.slotHours !== null ? `${num(c.slotHours, 3)}-hour ` : ''}UTC slot of the day, each frame against the mean of its own month and hours, so the daily cycle is not read as change`))
  } else if (c.grouping === 'annual') {
    out.push(sentence('Each grid cell\'s mean was computed over the period\'s annual values'))
  } else {
    out.push(sentence(`Each grid cell's mean was computed over every frame of the window, ${c.windowReason === 'tooFine' ? 'since the data have more than eight frames a day, so the mean includes the daily cycle' : 'which is too short for calendar months'}; a window has no year-to-year spread, so no σ was estimated`))
  }
  if (c.grouping === 'window') {
    out.push('A cell has a mean only where it has data in at least 80 % of the frames, frames the publisher filled, interpolated, estimated or lists as missing counting as missing.')
  } else {
    out.push(sentence(`A cell has a mean only where it has data in at least 80 % of the frames of its ${group}, frames the publisher filled, interpolated, estimated or lists as missing counting as missing (WMO, 2017; its rule on consecutive missing years was not applied)`))
  }
  cite('welford1962', 'west1979')
  out.push(sentence(`Means and co-moments were accumulated cell by cell with Welford's (1962) running algorithm as extended to co-moments by West (1979), in single precision${c.doubleMean ? ' (the value\'s mean in double precision, since a group holds more than 255 frames)' : ''}`))
  if (c.sigma) {
    cite('hawkinsSutton2012')
    const spread = c.grouping === 'daily'
      ? 'the spread of that month\'s daily values across the period, day to day and year to year'
      : c.grouping === 'subdaily'
        ? 'the spread of the values of that month and slot across the period, day to day and year to year'
        : 'the year-to-year spread'
    out.push(sentence(`σ is the standard deviation of each cell's values in its ${group} about the period's linear trend, on n − 2 degrees of freedom (after Hawkins & Sutton, 2012, here with a linear trend over the period): ${spread}; it needs at least 10 values in the cell and 10 years in the ${group}`))
    const share = c.sigmaCells > 0 ? `; ${num((100 * c.flooredCells) / c.sigmaCells, 2)} %` : ''
    out.push(sentence(`σ is never below one quantisation step at the mean, so it includes the data's rounding and streaming noise; ${c.flooredCells.toLocaleString('en-GB')} of the ${c.sigmaCells.toLocaleString('en-GB')} values of σ (one per cell and ${group}${share}) were raised to that floor, where the standardised change understates how unusual a value is`))
    if (c.minYears < 30) out.push(sentence(`The period has ${c.minYears} years in its least-covered ${group}, fewer than the 30 of a WMO normal, so its mean and σ are less steady`))
  } else if (c.grouping !== 'window') {
    out.push(sentence(`No σ was estimated: the period has fewer than 10 years in some ${group}`))
  }
  out.push('The climatology was held as 16-bit numbers per cell: the mean as a fractional code of the data\'s own value table (read back by interpolating it) and σ as a multiple of the quantisation step at the mean.')
  if (c.factor > 1) {
    out.push(sentence(`To fit in 256 MiB of memory, the climatology kept one grid cell in ${c.factor} in each direction (the cell at each block's centre), and frames were compared with it at those cells, so its comparisons have that resolution`))
  }
  return out
}

/** The region as a phrase, with its box when it has one. */
function regionText(region: MethodsRegion): string {
  if (region.kind === 'dataset' || !region.bounds) return 'the whole dataset'
  const box = boxText(region.bounds)
  if (region.kind === 'box') return `a box from ${box}`
  if (region.kind === 'view') return `the area in view (${box})`
  // AYNI (2026-10-18): a country by its outline, not its box.
  if (region.kind === 'country') return `${region.label} (the grid cells whose centres fall inside its Natural Earth 1:50m outline, within ${box})`
  return `${region.label} (${box})`
}

/** AYNI XR as software, APA: maker (year). Title (Version) [Computer software]. DOI. */
export function softwareReference(input: Pick<MethodsInput, 'software' | 'softwareDoi' | 'accessed'>): string {
  const version = /^AYNI XR\s+(\S+)$/.exec(input.software)?.[1] ?? null
  const doi = input.softwareDoi?.trim()
  return `${AYNI_XR_PUBLISHER} (${input.accessed.getUTCFullYear()}). AYNI XR${version ? ` (Version ${version})` : ''} [Computer software].${doi ? ` https://doi.org/${doi}` : ''}`
}

/** The paragraph and the reference list as one text, for copying and for the file. */
export function methodsText(input: MethodsInput): string {
  const m = buildMethods(input)
  return `${m.paragraph}\n\nReferences\n\n${m.references.join('\n\n')}\n`
}
